/**
 *
 * StaffEditorModal
 *
 * Create / edit a Staff member. Self-contained: owns its form state,
 * validation and the create/update request. The parent only controls
 * visibility, passes the staff being edited (null = create) and is
 * notified via onSuccess so it can refresh the list.
 *
 */

import React from "react"
import {
	Modal,
	Form,
	Input,
	Select,
	InputNumber,
	notification,
	Avatar,
	Button,
	Divider,
	Spin,
	Empty,
	Tag,
	Tooltip,
} from "antd"
import { FormInstance } from "antd/lib/form"
import { Icon, PhoneInput } from "components"

import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"

// Sentinel used both as the "All contracts" option value and as the value stored
// in the Firebase claim for a business the user has full access to.
const ALL_CONTRACTS = "all"

// Roles that can be assigned from the backoffice. `sysadmin` is intentionally
// not assignable here, but getRoleLabel still resolves it for display.
export const ASSIGNABLE_ROLES = [
	"admin",
	"manager",
	"supervisor",
	"dispatcher",
	"driver",
	"operator",
	"dashboard-user",
	"service-account",
]

// Roles whose business/contract grants can be edited from this modal. Every role
// still sees its grants, read-only — drivers and operators get theirs from the
// contract they are assigned to, and the API rejects the field for anyone else.
export const BUSINESS_SCOPED_ROLES = ["dashboard-user", "service-account"]

export function getRoleLabel(role: string) {
	switch (role) {
		case "admin":
			return Strings.staff.admin
		case "manager":
			return Strings.staff.manager
		case "sysadmin":
			return Strings.staff.sysAdmin
		case "supervisor":
			return Strings.staff.supervisor
		case "dispatcher":
			return Strings.staff.dispatcher
		case "driver":
			return Strings.staff.driver
		case "operator":
			return Strings.staff.operator
		case "dashboard-user":
			return Strings.staff.dashboardUser
		case "service-account":
			return Strings.staff.serviceAccount
		default:
			return role
	}
}

interface Props {
	visible: boolean
	staff: any
	onClose: () => void
	onSuccess: () => void
}

interface FirebaseBusiness {
	id: string
	name: string
	allContracts: boolean
	contracts: Array<{ id: string; name: string }>
}

interface FirebaseInfo {
	photoURL: string | null
	displayName: string | null
	email: string | null
	businesses: FirebaseBusiness[]
}

// A business as offered in the pickers, with the contracts that can be assigned.
interface BusinessOption {
	_id: string
	name: string
	contracts: Array<{ _id: string; name: string }>
}

// One row of the businesses Form.List. `contracts` holds contract ids, or the
// single ALL_CONTRACTS sentinel.
interface BusinessRow {
	businessId?: string
	contracts?: string[]
}

interface State {
	submitting: boolean
	firebaseLoading: boolean
	firebaseInfo: FirebaseInfo | null
	businessesLoading: boolean
	businessOptions: BusinessOption[]
}

export class StaffEditorModal extends React.Component<Props, State> {
	formRef = React.createRef<FormInstance>()

	constructor(props: Props) {
		super(props)

		this.state = {
			submitting: false,
			firebaseLoading: false,
			firebaseInfo: null,
			businessesLoading: false,
			businessOptions: [],
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { visible, staff } = this.props

		// Fetch the Firebase view only when the modal transitions to open.
		if (visible && !prevProps.visible) {
			if (staff?._id) {
				this.loadBusinessOptions()
				this.loadFirebaseInfo(staff._id)
			} else {
				this.setState({ firebaseInfo: null, firebaseLoading: false })
			}
		}
	}

	loadBusinessOptions = async () => {
		this.setState({ businessesLoading: true })

		try {
			const response = await API.get({
				url: Endpoints.v2.uriBusinesses({ includeContracts: true }),
			})

			if (response.ok) {
				this.setState({
					businessOptions: (response.data.results || []).map((biz: any) => ({
						_id: String(biz._id),
						name: biz.name,
						contracts: (biz.contracts || []).map((contract: any) => ({
							_id: String(contract._id),
							name: contract.name,
						})),
					})),
				})
			}
		} catch (err) {
			// Non-fatal: the pickers just have nothing to offer.
			console.log("Failed to load businesses", err)
		}

		this.setState({ businessesLoading: false })
	}

	loadFirebaseInfo = async (id: string) => {
		this.setState({ firebaseLoading: true, firebaseInfo: null })

		try {
			const response = await API.get({ url: Endpoints.uriStaff(id) })

			if (response.ok) {
				const staff = response.data.results.staff
				this.setState({
					firebaseInfo: staff
						? {
								photoURL: staff.photo ?? null,
								displayName: staff.name ?? null,
								email: staff.email ?? null,
								businesses: staff.businesses ?? [],
							}
						: null,
				})

				// The claims arrive after the form mounted, so seed the list here
				// instead of through initialValues.
				this.formRef.current?.setFieldsValue({
					businesses: (staff?.businesses ?? []).map(
						(biz: FirebaseBusiness): BusinessRow => ({
							businessId: biz.id,
							contracts: biz.allContracts
								? [ALL_CONTRACTS]
								: (biz.contracts || []).map((contract) => contract.id),
						})
					),
				})
			}
		} catch (err) {
			// Non-fatal: the section just stays hidden.
			console.log("Failed to load Firebase info", err)
		}

		this.setState({ firebaseLoading: false })
	}

	// "All contracts" is exclusive: picking it drops the explicit contracts, and
	// picking a contract while it is set drops it. Runs as a Form.Item `normalize`
	// so it sees the value before the change was committed to the store.
	normalizeContracts = (value: string[], prevValue: string[] = []) => {
		if (!value?.includes(ALL_CONTRACTS)) return value

		return prevValue.includes(ALL_CONTRACTS)
			? value.filter((v) => v !== ALL_CONTRACTS)
			: [ALL_CONTRACTS]
	}

	// Contracts belong to a business, so a different business invalidates them.
	handleBusinessChange = (index: number) => {
		const form = this.formRef.current
		if (!form) return

		const rows: BusinessRow[] = [...(form.getFieldValue("businesses") || [])]
		rows[index] = { ...rows[index], contracts: [] }
		form.setFieldsValue({ businesses: rows })
	}

	handleSubmit = async () => {
		const { staff, onSuccess } = this.props
		const form = this.formRef.current

		if (!form) return

		let values: any
		try {
			values = await form.validateFields()
		} catch {
			// antd renders field-level errors; nothing else to do.
			return
		}

		const isEdit = Boolean(staff?._id)

		this.setState({ submitting: true })

		try {
			const body = new FormData()

			body.append("name", values.name)
			body.append("email", values.email)
			body.append("role", values.role)

			if (values.phone) body.append("phone", values.phone)
			if (!isEdit && values.password) body.append("password", values.password)
			if (values.expectedWorkingHours != null)
				body.append("expectedWorkingHours", String(values.expectedWorkingHours))
			if (values.expectedWorkingDays != null)
				body.append("expectedWorkingDays", String(values.expectedWorkingDays))

			// Firebase claim shape: { [businessId]: "all" | [contractId, ...] }.
			// Sent whenever the section is editable, so clearing every row revokes
			// the access; omitted for the other roles, whose grants are not ours.
			if (isEdit && BUSINESS_SCOPED_ROLES.includes(values.role)) {
				const businesses: Record<string, string | string[]> = {}
				;(values.businesses || []).forEach((row: BusinessRow) => {
					if (!row?.businessId) return
					businesses[row.businessId] = row.contracts?.includes(ALL_CONTRACTS)
						? ALL_CONTRACTS
						: row.contracts || []
				})
				body.append("businesses", JSON.stringify(businesses))
			}

			const request = isEdit ? API.put : API.post

			const response = await request({
				url: Endpoints.uriStaff(staff?._id || ""),
				data: body,
			})

			if (response.ok && response.data.results.staff) {
				notification.success({
					message: isEdit ? Strings.staff.editStaff : Strings.staff.createStaff,
					description: isEdit
						? Strings.staff.editedSuccessfully
						: Strings.staff.createdSuccessfully,
					placement: "bottomRight",
					duration: 5,
				})
				onSuccess()
			} else {
				notification.error({
					message: Strings.serverErrors.wentWrong,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		this.setState({ submitting: false })
	}

	// Contracts of the business selected on this row, plus the "All contracts"
	// option. Values with no matching option (a contract that has since been
	// removed) are still held by the field, so re-saving doesn't silently drop them.
	renderContractsField(index: number, businessId?: string) {
		const { businessOptions } = this.state

		const business = businessOptions.find((biz) => biz._id === businessId)

		return (
			<Form.Item
				label={Strings.businesses.contracts}
				name={[index, "contracts"]}
				fieldKey={[index, "contracts"]}
				style={{ marginBottom: 0 }}
				normalize={this.normalizeContracts}
				rules={[
					{
						validator: (_: any, value: string[]) =>
							value && value.length
								? Promise.resolve()
								: Promise.reject(new Error(Strings.staff.contractsRequired)),
					},
				]}
			>
				<Select
					mode="multiple"
					allowClear
					disabled={!businessId}
					placeholder={Strings.businesses.contracts}
					optionFilterProp="children"
					filterOption={(input: any, option: any) =>
						String(option.children)
							.toLowerCase()
							.indexOf(input.toLowerCase()) >= 0
					}
				>
					<Select.Option key={ALL_CONTRACTS} value={ALL_CONTRACTS}>
						{Strings.staff.allContracts}
					</Select.Option>
					{(business?.contracts || []).map((contract) => (
						<Select.Option key={contract._id} value={contract._id}>
							{contract.name}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
		)
	}

	// What the other roles get: their current grants, without the pickers. Reads
	// the loaded claims rather than the form, which is what stays saved for them.
	renderBusinessesReadOnly() {
		const businesses = this.state.firebaseInfo?.businesses || []

		if (businesses.length === 0) {
			return (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={Strings.staff.noBusinessesAssigned}
				/>
			)
		}

		return businesses.map((biz) => (
			<div key={biz.id} style={{ marginBottom: 12 }}>
				<div style={{ fontWeight: 600, marginBottom: 4 }}>{biz.name}</div>
				{biz.allContracts ? (
					<Tag color="blue">{Strings.staff.allContracts}</Tag>
				) : biz.contracts.length === 0 ? (
					<span style={{ color: "#999" }}>—</span>
				) : (
					biz.contracts.map((contract) => (
						<Tag key={contract.id} style={{ marginBottom: 4 }}>
							{contract.name}
						</Tag>
					))
				)}
			</div>
		))
	}

	renderBusinessesEditor() {
		const { businessOptions } = this.state

		return (
			<Form.List name="businesses">
				{(fields, { add, remove }) => (
					<>
						{fields.length === 0 && (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description={Strings.staff.noBusinessesAssigned}
							/>
						)}
						{fields.map((field) => (
							<div
								key={field.key}
								style={{
									border: "1px solid #f0f0f0",
									borderRadius: 4,
									padding: "12px 12px 0",
									marginBottom: 12,
									position: "relative",
								}}
							>
								<Tooltip title={Strings.generic.remove}>
									<div
										style={{
											position: "absolute",
											top: 8,
											right: 12,
											cursor: "pointer",
											zIndex: 1,
										}}
										onClick={() => remove(field.name)}
									>
										<Icon name="trash" />
									</div>
								</Tooltip>
								<Form.Item
									label={Strings.businesses.business}
									name={[field.name, "businessId"]}
									fieldKey={[field.fieldKey, "businessId"]}
									rules={[
										{
											required: true,
											message: Strings.staff.businessRequired,
										},
										({ getFieldValue }: any) => ({
											validator: (_: any, value: string) => {
												if (!value) return Promise.resolve()
												const rows: BusinessRow[] =
													getFieldValue("businesses") || []
												const used = rows.filter(
													(row) => row?.businessId === value
												).length
												return used > 1
													? Promise.reject(
															new Error(Strings.staff.duplicatedBusiness)
														)
													: Promise.resolve()
											},
										}),
									]}
								>
									<Select
										showSearch
										placeholder={Strings.businesses.business}
										optionFilterProp="children"
										onChange={() => this.handleBusinessChange(field.name)}
										filterOption={(input: any, option: any) =>
											String(option.children)
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									>
										{businessOptions.map((biz) => (
											<Select.Option key={biz._id} value={biz._id}>
												{biz.name}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
								<Form.Item
									noStyle
									shouldUpdate={(prev: any, cur: any) =>
										prev.businesses?.[field.name]?.businessId !==
										cur.businesses?.[field.name]?.businessId
									}
								>
									{({ getFieldValue }: any) =>
										this.renderContractsField(
											field.name,
											getFieldValue(["businesses", field.name, "businessId"])
										)
									}
								</Form.Item>
								<div style={{ height: 12 }} />
							</div>
						))}
						<Button
							type="dashed"
							block
							onClick={() => add({ contracts: [] })}
							style={{ marginBottom: 8 }}
						>
							<Icon name="plus" style={{ marginRight: 8 }} />
							{Strings.staff.addBusiness}
						</Button>
					</>
				)}
			</Form.List>
		)
	}

	renderBusinessesSection(editable: boolean) {
		const { firebaseLoading, businessesLoading } = this.state

		if (firebaseLoading || businessesLoading) {
			return (
				<div style={{ textAlign: "center", padding: "16px 0" }}>
					<Spin />
				</div>
			)
		}

		return (
			<>
				<Divider style={{ margin: "12px 0" }} orientation="left">
					{Strings.staff.businessesAndContracts}
				</Divider>
				{editable
					? this.renderBusinessesEditor()
					: this.renderBusinessesReadOnly()}
			</>
		)
	}

	render() {
		const { visible, staff, onClose } = this.props
		const { submitting, firebaseInfo } = this.state

		const isEdit = Boolean(staff?._id)

		return (
			<Modal
				visible={visible}
				width={520}
				maskClosable={false}
				destroyOnClose
				confirmLoading={submitting}
				okText={Strings.generic.confirm}
				cancelText={Strings.generic.cancel}
				onOk={this.handleSubmit}
				onCancel={onClose}
				title={
					<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<Icon name="user2" />
						{isEdit ? Strings.staff.editStaff : Strings.staff.addStaff}
					</span>
				}
			>
				{isEdit && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginBottom: 16,
						}}
					>
						<Avatar
							size={88}
							src={firebaseInfo?.photoURL || undefined}
							icon={<Icon name="user2" />}
						/>
					</div>
				)}
				<Form
					ref={this.formRef}
					layout="vertical"
					initialValues={{
						name: staff?.name || "",
						email: staff?.email || "",
						phone: staff?.phone || "",
						role: staff?.role || undefined,
						expectedWorkingHours: staff?.expectedWorkingHours,
						expectedWorkingDays: staff?.expectedWorkingDays,
						businesses: [],
					}}
				>
					<Form.Item
						label={Strings.fields.name}
						name="name"
						rules={[{ required: true, message: Strings.errors.fillField }]}
					>
						<Input placeholder={Strings.fields.name} />
					</Form.Item>

					<Form.Item
						label={Strings.fields.email}
						name="email"
						rules={[
							{ required: true, message: Strings.errors.fillField },
							{ type: "email", message: Strings.errors.emailIsInvalid },
						]}
					>
						<Input placeholder={Strings.fields.email} disabled={isEdit} />
					</Form.Item>

					<Form.Item label={Strings.fields.phone} name="phone">
						<PhoneInput defaultCountry="pt" />
					</Form.Item>

					{!isEdit && (
						<>
							<Form.Item label={Strings.fields.password} name="password">
								<Input.Password placeholder={Strings.fields.password} />
							</Form.Item>
							<Form.Item
								noStyle
								shouldUpdate={(prev: any, cur: any) =>
									prev.password !== cur.password
								}
							>
								{({ getFieldValue }) =>
									!getFieldValue("password")?.trim() ? (
										<div
											style={{
												marginTop: -8,
												marginBottom: 16,
												fontSize: 12,
												color: "#8a6d3b",
												lineHeight: 1.4,
											}}
										>
											{Strings.staff.noPasswordWarning}
										</div>
									) : null
								}
							</Form.Item>
						</>
					)}

					<Form.Item
						label={Strings.profile.role}
						name="role"
						rules={[{ required: true, message: Strings.errors.fillField }]}
					>
						<Select
							placeholder={Strings.placeholders.role}
							showSearch
							optionFilterProp="children"
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						>
							{ASSIGNABLE_ROLES.map((role: string) => (
								<Select.Option key={role} value={role}>
									{getRoleLabel(role)}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						noStyle
						shouldUpdate={(prev: any, cur: any) => prev.role !== cur.role}
					>
						{({ getFieldValue }) =>
							getFieldValue("role") === "driver" ? (
								<>
									<Form.Item
										label={Strings.staff.expectedWorkingHours}
										name="expectedWorkingHours"
									>
										<InputNumber
											style={{ width: "100%" }}
											type="number"
											min={0}
											max={24}
											placeholder={Strings.staff.expectedWorkingHours}
										/>
									</Form.Item>
									<Form.Item
										label={Strings.staff.expectedWorkingDays}
										name="expectedWorkingDays"
									>
										<InputNumber
											style={{ width: "100%" }}
											type="number"
											min={0}
											max={7}
											placeholder={Strings.staff.expectedWorkingDays}
										/>
									</Form.Item>
								</>
							) : null
						}
					</Form.Item>

					{isEdit && (
						<Form.Item
							noStyle
							shouldUpdate={(prev: any, cur: any) => prev.role !== cur.role}
						>
							{({ getFieldValue }: any) =>
								this.renderBusinessesSection(
									BUSINESS_SCOPED_ROLES.includes(getFieldValue("role"))
								)
							}
						</Form.Item>
					)}
				</Form>
			</Modal>
		)
	}
}

export default StaffEditorModal
